import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// ---------- PWA origin / backend-URL migration self-heal ----------
// Users who installed the PWA on an older domain (or an older build that
// pointed at a now-dead backend host) get stuck: cached JS/HTML + a bound
// service worker keep them pinned to a URL that no longer answers, and
// WebAuthn credentials are origin-bound so biometric login also fails.
//
// On every boot we compare the backend URL baked into THIS bundle against
// a fingerprint we stored on the last successful boot. If they differ we
// treat the app as "migrated" and:
//   1. drop all Cache Storage entries (stale HTML shell + old JS chunks),
//   2. unregister the old service worker(s),
//   3. wipe auth artefacts (`pr_token`, `pr_user`, cached WebAuthn hints),
//   4. bounce to /login?migrated=1 so the user sees a one-line banner and
//      is prompted to sign in + re-enroll biometrics on the new origin.
// Then a full reload lands them on a clean slate.
(() => {
  try {
    const currentBackend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    const currentOrigin = window.location.origin;
    const fingerprint = `${currentOrigin}|${currentBackend}`;
    const KEY = "pr_pwa_origin_fp";
    const prev = localStorage.getItem(KEY);

    if (prev && prev !== fingerprint) {
      // Migration detected — self-heal.
      const purge = Promise.all([
        "caches" in window
          ? caches.keys().then((ks) => Promise.all(ks.map((k) => caches.delete(k))))
          : Promise.resolve(),
        "serviceWorker" in navigator
          ? navigator.serviceWorker.getRegistrations().then((rs) =>
              Promise.all(rs.map((r) => r.unregister()))
            )
          : Promise.resolve(),
      ]);

      // Wipe auth + WebAuthn hints. Preserve non-auth prefs so users don't
      // lose UI settings for a domain rename that doesn't actually change
      // who they are.
      ["pr_token", "pr_user", "webauthn_last_user", "webauthn_credential_hint"].forEach((k) => {
        try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
      });

      localStorage.setItem(KEY, fingerprint);
      purge.finally(() => {
        // Only redirect if we aren't already at login — avoids loops.
        if (!window.location.pathname.startsWith("/login")) {
          window.location.replace("/login?migrated=1");
        } else {
          window.location.reload();
        }
      });
      return; // stop mount — reload will restart cleanly.
    }
    // First boot on this origin/backend combo, or unchanged — just record.
    localStorage.setItem(KEY, fingerprint);
  } catch (_) { /* localStorage disabled / private mode — skip gracefully */ }
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register the PWA service worker so the app is installable on iOS / Android.
// We deliberately exclude /api from caching (see public/service-worker.js).
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((reg) => {
        // Check for updates immediately on every load AND every 5 minutes
        // while the tab is open. After a deploy this guarantees the new SW
        // gets noticed within ~5 min even on long-lived tabs.
        reg.update().catch(() => {});
        setInterval(() => { reg.update().catch(() => {}); }, 5 * 60 * 1000);

        // When a NEW SW finishes installing while the OLD one is still in
        // control, ask the new one to skipWaiting so it takes over now. The
        // `controllerchange` listener below then triggers a single page
        // reload — net effect: deploys propagate within seconds.
        reg.addEventListener("updatefound", () => {
          const sw = reg.installing;
          if (!sw) return;
          sw.addEventListener("statechange", () => {
            if (sw.state === "installed" && navigator.serviceWorker.controller) {
              try { sw.postMessage("SKIP_WAITING"); } catch (_) { /* ignore */ }
            }
          });
        });
      })
      .catch((err) => console.warn("SW register failed", err));
  });
  // When a new SW takes control (after skipWaiting), reload exactly once so
  // the page swaps to the latest HTML/JS without showing a stale screen.
  let _reloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (_reloaded) return;
    _reloaded = true;
    window.location.reload();
  });

  // Safety net for the "stale shell" white-screen: if the page's main JS
  // bundle 404s (the cached index.html is pointing at hashes that no longer
  // exist on the server), aggressively reset the cache + SW and reload.
  // Only triggers when a hashed `static/js/main.*.js` chunk fails to load.
  window.addEventListener("error", (e) => {
    const tgt = e && e.target;
    if (!tgt || (tgt.tagName !== "SCRIPT" && tgt.tagName !== "LINK")) return;
    const src = tgt.src || tgt.href || "";
    if (!/\/static\/(js|css)\//.test(src)) return;
    // Guard against reload loops.
    if (sessionStorage.getItem("sw_reset_attempted")) return;
    sessionStorage.setItem("sw_reset_attempted", "1");
    Promise.all([
      "caches" in window ? caches.keys().then((ks) => Promise.all(ks.map((k) => caches.delete(k)))) : Promise.resolve(),
      navigator.serviceWorker.getRegistrations().then((rs) => Promise.all(rs.map((r) => r.unregister()))),
    ]).finally(() => window.location.reload());
  }, true);

  // If a previous load triggered the reset, clear the guard once the page
  // actually renders so future deploys can self-heal again.
  window.addEventListener("load", () => {
    setTimeout(() => sessionStorage.removeItem("sw_reset_attempted"), 4000);
  });
}
